import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// @material-ui/core icons
import Refresh from '@material-ui/icons/Refresh';
import Reprocess from '@material-ui/icons/SettingsBackupRestore';
// @material-ui/core components
import userProfileStyles from 'assets/jss/material-dashboard-pro-react/views/userProfileStyles';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Timeline from 'components/UI/Timeline/Timeline';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import Card from 'components/UI/Card/Card';
// our components
import AdInfo from 'components/common/AdInfo';
import AdUser from 'components/common/AdUser';
import AdActions from 'components/common/AdActions';
import AdInfoSidebar from 'components/common/AdInfoSidebar';
import { LoadingAnimation } from 'components/common/Loading';
import WithPermission from 'components/User/WithPermission';
import Notes from 'components/common/Notes';
import { adStatus, notesEntities } from 'config';

class AdDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'details',
    };
  }

  /**
   * @description
   * Call refresh callback.
   */
  handleRefresh = () => {
    const { refresh } = this.props;
    refresh();
  };

  RenderTabsContent = props => {
    const { children } = props;
    const { activeTab } = this.state;
    return children.find(child => child.props.label === activeTab);
  };

  /**
   * @description
   * Render Details Ad.
   */
  render() {
    const { activeTab } = this.state;
    const {
      classes,
      data,
      adReview,
      adUnReview,
      adPublish,
      adUnPublish,
      adReprocess,
      adActionLoading,
      t,
    } = this.props;
    return (
      <React.Fragment>
        <GridContainer justify="space-between" alignItems="center">
          <GridItem>
            <Button
              data-cy="details-tab"
              color="primary"
              simple={activeTab !== 'details'}
              round
              id="details-tab"
              className={classes.logsBtn}
              onClick={() => this.setState({ activeTab: 'details' })}
            >
              {t('ad.details.tabs.section')}
              <span>{t('ad.details.tabs.section_break')}</span>
            </Button>

            <Button
              data-cy="logs-tab"
              color="primary"
              simple={activeTab !== 'logs'}
              round
              id="logs-tab"
              onClick={() => this.setState({ activeTab: 'logs' })}
            >
              {t('ad.details.tabs.log')}
            </Button>
            <WithPermission allowPermissions={['list_note']} alt={null}>
              <Button
                data-cy="notes-tab"
                color="primary"
                simple={activeTab !== 'notes'}
                round
                id="notes-tab"
                onClick={() => this.setState({ activeTab: 'notes' })}
              >
                {t('ad.details.tabs.notes')}
              </Button>
            </WithPermission>
          </GridItem>
          <GridItem>
            <Button
              color="primary"
              simple
              className={classes.showFilters}
              onClick={this.handleRefresh}
              id="refresh"
              key="refresh"
            >
              <Refresh />
              {t('ad.filters.update')}
            </Button>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={8} lg={8}>
            <this.RenderTabsContent>
              <div label="logs">
                <Timeline data-cy="logs" stories={data.logs} />
              </div>
              <div label="details">
                <AdInfo data={data} />
              </div>
              <div label="notes">
                <WithPermission allowPermissions={[]}>
                  {data.ad && (
                    <Notes
                      entityID={data.ad.id}
                      entity={
                        data.ad.status === 'EXPIRED'
                          ? notesEntities.EXPIRED_AD
                          : notesEntities.AD
                      }
                      allNotes={data.ad.notes || []}
                    />
                  )}
                </WithPermission>
              </div>
            </this.RenderTabsContent>
          </GridItem>
          <GridItem xs={12} sm={12} md={4} lg={4}>
            {data.ad && data.ad.status !== adStatus.EXPIRED && (
              <div>
                <AdActions
                  data={data}
                  adReview={adReview}
                  adUnReview={adUnReview}
                  adPublish={adPublish}
                  adUnPublish={adUnPublish}
                  adActionLoading={adActionLoading}
                />

                <WithPermission allowPermissions={['review_ad']}>
                  <GridContainer>
                    <GridItem xs={12}>
                      <Card className={classes.permissionsContainer}>
                        {adActionLoading && (
                          <div className={classes.actionsLoader}>
                            <LoadingAnimation />
                          </div>
                        )}
                        <div>
                          <h6>{t('ad.details.reprocess')}</h6>
                          <Button
                            data-cy="adReprocessButton"
                            color="warning"
                            id="ads-reprocess"
                            onClick={() => adReprocess([data.ad.id])}
                          >
                            <Reprocess />
                            {t('common.reprocess')}
                          </Button>
                        </div>
                      </Card>
                    </GridItem>
                  </GridContainer>
                </WithPermission>
              </div>
            )}

            <AdUser data={data} />
            <AdInfoSidebar data={data} />
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

/**
 * AdDetails PropTypes
 * @param classes - styles
 * @param data - ad query's results
 * @param t - prop from HOC with string translations
 * @callback adReview - callback to review elements
 * @callback adUnReview - callback to unreview elements
 * @callback adPublish - callback to publish elements
 * @callback adUnPublish - callback to unpublish elements
 * @callback adReprocess - callback to reprocess elements
 * @callback refresh - callback to refresh page
 * @callback adActionLoading - boolean to show loading when user taps on action
 */
AdDetails.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({}).isRequired,
  adReview: PropTypes.func.isRequired,
  adUnReview: PropTypes.func.isRequired,
  adPublish: PropTypes.func.isRequired,
  adUnPublish: PropTypes.func.isRequired,
  adReprocess: PropTypes.func.isRequired,
  adActionLoading: PropTypes.bool.isRequired,
  refresh: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to AdDetails component
// This is a small hack, with intermediary Component to resolved it temporally
const AdDetailsWrapper = props => <AdDetails {...props} />;

export default withTranslation()(
  withStyles(userProfileStyles)(AdDetailsWrapper),
);
